import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * Formulário do PDP: seleção de variante, quantidade e envio pro carrinho.
 * Quantidade respeita estoque (inventory_management + policy deny).
 */
class BdlPdpForm extends HTMLElement {
  connectedCallback() {
    this.options = Array.from(this.querySelectorAll('[data-bdl-pdp-option]'));
    this.variantIdInput = this.querySelector('[data-bdl-pdp-variant-id]');
    this.submitBtn = this.querySelector('[data-bdl-pdp-submit]');
    this.errorEl = this.querySelector('[data-bdl-pdp-form-error]');
    this.pricePrompt = this.closest('.bdl-pdp-main__details')?.querySelector('[data-bdl-pdp-price-prompt]');
    this.priceBox = this.closest('.bdl-pdp-main__details')?.querySelector('[data-bdl-pdp-price]');
    this.priceCurrent = this.closest('.bdl-pdp-main__details')?.querySelector('[data-bdl-pdp-price-current]');
    this.priceCompare = this.closest('.bdl-pdp-main__details')?.querySelector('[data-bdl-pdp-price-compare]');
    this.weightChip = this.closest('.bdl-pdp-main__details')?.querySelector('[data-bdl-pdp-weight-value]');
    this.qtyInput = this.querySelector('[data-bdl-pdp-qty-input]');
    this.qtyMinus = this.querySelector('[data-bdl-pdp-qty-minus]');
    this.qtyPlus = this.querySelector('[data-bdl-pdp-qty-plus]');
    this.cartAddUrl = this.dataset.cartAddUrl || '/cart/add';
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || 'BRL';

    this.options.forEach((option) => {
      option.addEventListener('change', () => this.onOptionChange(option));
    });

    this.qtyMinus?.addEventListener('click', () => this.stepQty(-1));
    this.qtyPlus?.addEventListener('click', () => this.stepQty(1));
    this.qtyInput?.addEventListener('change', () => this.clampQty({ announceMax: true }));
    this.qtyInput?.addEventListener('input', () => this.clampQty({ announceMax: true }));

    this.submitBtn?.addEventListener('click', (event) => this.onSubmit(event));
    this.syncAvailabilityUi();
  }

  /**
   * Variante selecionada (ou a única do form) está à venda?
   * Com opções e nenhuma marcada → ainda não dá pra comprar.
   * @returns {boolean}
   */
  isAvailable() {
    if (this.options.length) {
      const checked = this.querySelector('[data-bdl-pdp-option]:checked');
      if (!checked) return false;
      return checked.dataset.available !== 'false';
    }
    return this.dataset.available !== 'false';
  }

  /**
   * CTA + qty conforme disponibilidade da variante atual.
   */
  syncAvailabilityUi() {
    const available = this.isAvailable();
    const awaitingOption = this.options.length > 0 && !this.querySelector('[data-bdl-pdp-option]:checked');
    const label = this.submitBtn?.querySelector('[data-bdl-pdp-submit-label]');
    const section = this.closest('.bdl-pdp-main');
    const productSoldOut =
      !this.options.length
        ? this.dataset.available === 'false'
        : this.options.every((opt) => opt.dataset.available === 'false');
    const showSoldOut = productSoldOut || (!awaitingOption && !available);

    if (this.submitBtn) {
      this.submitBtn.disabled = !available;
      this.submitBtn.classList.toggle('bdl-pdp-main__cta--soldout', showSoldOut);
      if (label) {
        if (awaitingOption) {
          label.textContent = 'Adicionar ao Carrinho';
        } else {
          label.textContent = available ? 'Adicionar ao Carrinho' : 'Esgotado';
        }
      }
    }

    if (this.qtyInput) this.qtyInput.disabled = !available;
    section?.classList.toggle('bdl-pdp-main--soldout', showSoldOut);

    this.syncQtyUi();
  }

  /**
   * Estoque da variante selecionada (ou default no form).
   * @returns {{ managed: boolean, policy: string, stock: number, cartQty: number }}
   */
  getInventory() {
    const checked = this.querySelector('[data-bdl-pdp-option]:checked');
    const source = checked || this;

    return {
      managed: source.dataset.inventoryManaged === 'true',
      policy: source.dataset.inventoryPolicy || 'deny',
      stock: Math.max(0, Number(source.dataset.inventoryQty) || 0),
      cartQty: Math.max(0, Number(source.dataset.cartQty) || 0),
    };
  }

  /**
   * Máximo que ainda pode adicionar agora. null = sem teto (não rastreia / vende sem estoque).
   * @returns {number | null}
   */
  getMaxQty() {
    const { managed, policy, stock, cartQty } = this.getInventory();
    if (!managed || policy !== 'deny') return null;
    return Math.max(0, stock - cartQty);
  }

  stockLimitMessage(_max) {
    return 'Você atingiu o máximo do estoque.';
  }

  syncQtyUi() {
    if (!this.qtyInput) return;
    const available = this.isAvailable();
    const max = this.getMaxQty();

    if (max === null) {
      this.qtyInput.removeAttribute('max');
    } else {
      this.qtyInput.max = String(Math.max(1, max));
    }

    const value = Math.max(1, Number(this.qtyInput.value) || 1);
    if (max !== null && value > max && max > 0) {
      this.qtyInput.value = String(max);
    }

    if (!available || max === 0) {
      if (this.qtyPlus) this.qtyPlus.disabled = true;
      if (this.qtyMinus) this.qtyMinus.disabled = true;
      this.qtyInput.disabled = true;
      return;
    }

    this.qtyInput.disabled = false;

    if (this.qtyPlus) {
      const atMax = max !== null && Number(this.qtyInput.value) >= max;
      this.qtyPlus.disabled = atMax;
    }
    if (this.qtyMinus) {
      this.qtyMinus.disabled = Number(this.qtyInput.value) <= 1;
    }
  }

  onOptionChange(option) {
    if (this.variantIdInput) this.variantIdInput.value = option.value;

    const price = Number(option.dataset.price || 0);
    const compare = Number(option.dataset.compare || 0);

    if (this.priceCurrent) this.priceCurrent.textContent = this.formatPrice(price);
    if (this.priceCompare) {
      if (compare > price) {
        this.priceCompare.textContent = this.formatPrice(compare);
        this.priceCompare.hidden = false;
      } else {
        this.priceCompare.hidden = true;
      }
    }
    if (this.weightChip && option.dataset.weight) {
      this.weightChip.textContent = option.dataset.weight;
    }

    this.pricePrompt?.setAttribute('hidden', '');
    this.priceBox?.removeAttribute('hidden');

    this.hideError();
    this.clampQty({ announceMax: false });
    this.syncAvailabilityUi();
  }

  formatPrice(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  stepQty(delta) {
    if (!this.qtyInput || !this.isAvailable()) return;
    const max = this.getMaxQty();
    const current = Math.max(1, Number(this.qtyInput.value) || 1);
    let next = current + delta;

    if (delta > 0 && max !== null && current >= max) {
      this.showError(this.stockLimitMessage(max));
      this.syncQtyUi();
      return;
    }

    next = Math.max(1, next);
    if (max !== null) next = Math.min(next, Math.max(1, max));

    this.qtyInput.value = String(next);

    if (max !== null && next >= max) {
      this.showError(this.stockLimitMessage(max));
    } else {
      this.hideError();
    }
    this.syncQtyUi();
  }

  clampQty({ announceMax = false } = {}) {
    if (!this.qtyInput) return;
    const max = this.getMaxQty();
    const raw = Number(this.qtyInput.value);
    let next = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
    let exceeded = false;

    next = Math.max(1, next);

    if (max !== null && max > 0 && next > max) {
      next = max;
      exceeded = true;
    }

    this.qtyInput.value = String(next);

    if (exceeded && announceMax) {
      this.showError(this.stockLimitMessage(max));
    } else if (!exceeded) {
      this.hideError();
    }

    this.syncQtyUi();
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  hideError() {
    if (!this.errorEl) return;
    this.errorEl.hidden = true;
  }

  bumpCartQty(variantId, added) {
    const option = this.options.find((el) => el.value === String(variantId));
    const target = option || this;
    const prev = Math.max(0, Number(target.dataset.cartQty) || 0);
    target.dataset.cartQty = String(prev + added);
    if (!option) {
      this.dataset.cartQty = String(prev + added);
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitBtn?.disabled || !this.isAvailable()) return;

    const variantId = this.variantIdInput?.value;
    if (!variantId) {
      this.showError('Selecione uma opção antes de continuar.');
      return;
    }

    this.clampQty({ announceMax: true });
    const max = this.getMaxQty();
    const quantity = Math.max(1, Number(this.qtyInput?.value) || 1);

    if (max !== null && max <= 0) {
      this.showError(this.stockLimitMessage(0));
      return;
    }

    if (max !== null && quantity > max) {
      this.showError(this.stockLimitMessage(max));
      return;
    }

    this.submitBtn?.setAttribute('disabled', '');

    try {
      const response = await fetch(this.cartAddUrl.replace(/\/?$/, '') + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });

      const data = await response.json();

      if (!response.ok) {
        this.showError(data.description || data.message || 'Não foi possível adicionar ao carrinho.');
        document.dispatchEvent(new CartErrorEvent(this.id || 'bdl-pdp-form', data.message, data.description, data.errors));
        return;
      }

      this.hideError();
      this.bumpCartQty(variantId, quantity);
      this.clampQty({ announceMax: false });

      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'bdl-pdp-form', {
          itemCount: cart.item_count,
          variantId: String(variantId),
        })
      );
    } catch (error) {
      this.showError('Não foi possível adicionar ao carrinho. Tente novamente.');
    } finally {
      this.syncAvailabilityUi();
    }
  }
}

/**
 * Calculadora de frete — hoje só formata o CEP; não há cálculo de frete real ligado ainda.
 */
class BdlPdpShipping extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-bdl-pdp-cep-input]');
    this.button = this.querySelector('[data-bdl-pdp-cep-submit]');
    this.result = this.parentElement?.querySelector('[data-bdl-pdp-cep-result]');

    this.input?.addEventListener('input', () => this.maskCep());
    this.button?.addEventListener('click', () => this.calculate());
  }

  maskCep() {
    if (!this.input) return;
    const digits = this.input.value.replace(/\D/g, '').slice(0, 8);
    this.input.value = digits.length > 5 ? `${digits.slice(0, 5)}-${digits.slice(5)}` : digits;
  }

  calculate() {
    if (!this.result) return;
    const digits = (this.input?.value || '').replace(/\D/g, '');
    if (digits.length !== 8) {
      this.result.textContent = 'Digite um CEP válido de 8 dígitos.';
      this.result.hidden = false;
      return;
    }
    this.result.textContent = 'Prazo e valor exatos são calculados no carrinho, na etapa de frete.';
    this.result.hidden = false;
  }
}

if (!customElements.get('bdl-pdp-shipping')) {
  customElements.define('bdl-pdp-shipping', BdlPdpShipping);
}
if (!customElements.get('product-form-bdl')) {
  customElements.define('product-form-bdl', BdlPdpForm);
}
